export type TalentType = 'S' | 'J' | 'P' | 'N';

export interface QuestionOption {
  text: string;
  type: TalentType;
  emoji: string;
}

export interface Question {
  id: number;
  question: string;
  options: QuestionOption[];
}

export interface CourseRec {
  courseId: number;
  title: string;
  desc: string;
  tag: string;
  icon: string;
}

export interface TalentResult {
  type: TalentType;
  name: string;
  subtitle: string;
  tagline: string;
  description: string;
  traits: string[];
  giftSigns: string[];
  color: string;
  gradientFrom: string;
  gradientTo: string;
  icon: string;
  courses: CourseRec[];
}

// ───── ENGLISH QUESTIONS ─────
export const questionsEn: Question[] = [
  {
    id: 1,
    question: 'When you walk into an unfamiliar space, what do you notice first?',
    options: [
      { text: 'Whether the atmosphere feels "off" or oppressive', type: 'S', emoji: '🌫️' },
      { text: 'Whether the layout follows some logic or pattern', type: 'J', emoji: '🔲' },
      { text: 'Whether a scent, plant, or object catches your attention', type: 'P', emoji: '🌿' },
      { text: 'Whether a certain person gives you a feeling', type: 'N', emoji: '✨' },
    ],
  },
  {
    id: 2,
    question: 'When someone is lying to you, you usually:',
    options: [
      { text: 'Sense something is "not right" intuitively', type: 'S', emoji: '💫' },
      { text: 'Spot logical inconsistencies through reasoning', type: 'J', emoji: '🔍' },
      { text: 'Notice changes in their behavior and mannerisms', type: 'P', emoji: '👀' },
      { text: 'Feel an emotional dissonance or incongruence', type: 'N', emoji: '💭' },
    ],
  },
  {
    id: 3,
    question: 'When you feel emotionally low, you tend to:',
    options: [
      { text: 'Shut out the world and retreat inward', type: 'S', emoji: '🪐' },
      { text: 'Analyze exactly what went wrong', type: 'J', emoji: '🧠' },
      { text: 'Do something to shift the state (light incense / shower / tidy up)', type: 'P', emoji: '🕯️' },
      { text: 'Get swept up by the energy of people or your environment', type: 'N', emoji: '🌊' },
    ],
  },
  {
    id: 4,
    question: 'Which "power" do you trust most?',
    options: [
      { text: 'Intuition & inspiration', type: 'S', emoji: '⚡' },
      { text: 'Structure & systems', type: 'J', emoji: '⚙️' },
      { text: 'Practice & ritual', type: 'P', emoji: '🔮' },
      { text: 'Energy flow & resonance', type: 'N', emoji: '💞' },
    ],
  },
  {
    id: 5,
    question: 'Which of these have you experienced?',
    options: [
      { text: 'Suddenly knowing an answer / a premonition came true', type: 'S', emoji: '🌟' },
      { text: 'Making accurate judgments through pure logic', type: 'J', emoji: '📐' },
      { text: 'Taking an action and reality actually shifted', type: 'P', emoji: '🌙' },
      { text: 'Having your emotions deeply affected by another person', type: 'N', emoji: '🫀' },
    ],
  },
  {
    id: 6,
    question: 'If you were to develop one ability, which appeals most?',
    options: [
      { text: 'Opening perception — seeing or receiving hidden information', type: 'S', emoji: '👁️' },
      { text: 'Mastering a complete system (astrology / tarot)', type: 'J', emoji: '📚' },
      { text: 'Blending, crafting, working hands-on with tools', type: 'P', emoji: '⚗️' },
      { text: 'Influencing energy, healing others or a space', type: 'N', emoji: '🌀' },
    ],
  },
  {
    id: 7,
    question: 'Your relationship with dreams is closest to:',
    options: [
      { text: 'Very vivid — sometimes carrying messages', type: 'S', emoji: '💭' },
      { text: 'You analyze the meaning behind your dreams', type: 'J', emoji: '📓' },
      { text: 'Dreams full of ritual or recurring specific scenes', type: 'P', emoji: '🔄' },
      { text: 'Intense emotional experiences that linger after waking', type: 'N', emoji: '❤️‍🔥' },
    ],
  },
  {
    id: 8,
    question: 'When someone moves into your proximity, you:',
    options: [
      { text: 'Sometimes feel "intruded upon" or that you\'re being sensed', type: 'S', emoji: '🛡️' },
      { text: 'Observe their logic and communication style', type: 'J', emoji: '🔭' },
      { text: 'Notice external details (scent / objects / appearance)', type: 'P', emoji: '🌸' },
      { text: 'Directly feel their emotions', type: 'N', emoji: '🤲' },
    ],
  },
  {
    id: 9,
    question: 'Which state are you most easily absorbed in?',
    options: [
      { text: 'Daydreaming, drifting, entering your inner world', type: 'S', emoji: '🌌' },
      { text: 'Thinking, analyzing, understanding', type: 'J', emoji: '💡' },
      { text: 'Operating, blending, making, creating', type: 'P', emoji: '🖐️' },
      { text: 'Feeling, connecting, resonating', type: 'N', emoji: '💜' },
    ],
  },
  {
    id: 10,
    question: 'Which of these do you experience most often?',
    options: [
      { text: '"I seem to see / sense something invisible"', type: 'S', emoji: '👁️' },
      { text: '"I seem to have understood a hidden pattern"', type: 'J', emoji: '🗺️' },
      { text: '"I did something and the outcome really changed"', type: 'P', emoji: '⚡' },
      { text: '"I was completely enveloped by an energy or emotion"', type: 'N', emoji: '🌊' },
    ],
  },
  {
    id: 11,
    question: 'If you could choose one gift, it would be:',
    options: [
      { text: 'Seeing what others cannot see', type: 'S', emoji: '🔮' },
      { text: 'Decoding complex systems', type: 'J', emoji: '📊' },
      { text: 'Changing real-world outcomes', type: 'P', emoji: '🪄' },
      { text: 'Influencing the energy of people and spaces', type: 'N', emoji: '💚' },
    ],
  },
  {
    id: 12,
    question: 'Which state frightens you most?',
    options: [
      { text: 'Too much information — unable to tell what is real', type: 'S', emoji: '📡' },
      { text: 'Complete chaos with no logic whatsoever', type: 'J', emoji: '🌀' },
      { text: 'Being unable to act or change anything', type: 'P', emoji: '⛓️' },
      { text: 'Being utterly controlled by emotions or by others', type: 'N', emoji: '🌪️' },
    ],
  },
];

// ───── CHINESE QUESTIONS ─────
export const questionsZh: Question[] = [
  {
    id: 1,
    question: '当你走进一个陌生的空间，你最先注意到什么？',
    options: [
      { text: '这里的氛围是否"不对劲"或有压迫感', type: 'S', emoji: '🌫️' },
      { text: '这里的布局是否遵循某种逻辑或规律', type: 'J', emoji: '🔲' },
      { text: '某个气味、植物或物件是否吸引了你', type: 'P', emoji: '🌿' },
      { text: '这里的某个人是否给你某种感觉', type: 'N', emoji: '✨' },
    ],
  },
  {
    id: 2,
    question: '当有人在对你撒谎时，你通常是：',
    options: [
      { text: '凭直觉"感觉有什么不对"', type: 'S', emoji: '💫' },
      { text: '通过逻辑推理发现前后矛盾', type: 'J', emoji: '🔍' },
      { text: '注意到对方行为举止的变化', type: 'P', emoji: '👀' },
      { text: '感受到情绪上的不协调感', type: 'N', emoji: '💭' },
    ],
  },
  {
    id: 3,
    question: '当你情绪低落时，你通常会：',
    options: [
      { text: '隔绝外界、退入内心', type: 'S', emoji: '🪐' },
      { text: '分析究竟是哪里出了问题', type: 'J', emoji: '🧠' },
      { text: '做些什么来转换状态（点香 / 洗澡 / 整理空间）', type: 'P', emoji: '🕯️' },
      { text: '容易被周围人或环境的能量所带动', type: 'N', emoji: '🌊' },
    ],
  },
  {
    id: 4,
    question: '你最相信哪种"力量"？',
    options: [
      { text: '直觉与灵感', type: 'S', emoji: '⚡' },
      { text: '结构与系统', type: 'J', emoji: '⚙️' },
      { text: '实践与仪式', type: 'P', emoji: '🔮' },
      { text: '能量流动与共振', type: 'N', emoji: '💞' },
    ],
  },
  {
    id: 5,
    question: '你是否有过以下经历？',
    options: [
      { text: '突然知道某个答案 / 预感成真', type: 'S', emoji: '🌟' },
      { text: '纯靠逻辑推理作出准确判断', type: 'J', emoji: '📐' },
      { text: '做了某件事，现实真的改变了', type: 'P', emoji: '🌙' },
      { text: '情绪被他人深度影响', type: 'N', emoji: '🫀' },
    ],
  },
  {
    id: 6,
    question: '如果你要发展一种能力，哪种最吸引你？',
    options: [
      { text: '开启感知——看见或接收隐藏信息', type: 'S', emoji: '👁️' },
      { text: '掌握一套完整系统（如占星/塔罗）', type: 'J', emoji: '📚' },
      { text: '调配、制作、动手操作工具', type: 'P', emoji: '⚗️' },
      { text: '影响能量、疗愈他人或空间', type: 'N', emoji: '🌀' },
    ],
  },
  {
    id: 7,
    question: '你与梦境的关系，最接近：',
    options: [
      { text: '非常生动——有时携带信息', type: 'S', emoji: '💭' },
      { text: '你会分析梦境的含义', type: 'J', emoji: '📓' },
      { text: '梦境充满仪式感或反复出现特定场景', type: 'P', emoji: '🔄' },
      { text: '强烈的情绪体验延续到醒来', type: 'N', emoji: '❤️‍🔥' },
    ],
  },
  {
    id: 8,
    question: '当有人靠近你时，你会：',
    options: [
      { text: '有时感到"被侵入"或被感知到', type: 'S', emoji: '🛡️' },
      { text: '观察对方的逻辑和沟通方式', type: 'J', emoji: '🔭' },
      { text: '注意对方的外在细节（气味 / 物件 / 外形）', type: 'P', emoji: '🌸' },
      { text: '直接感受到对方的情绪', type: 'N', emoji: '🤲' },
    ],
  },
  {
    id: 9,
    question: '你最容易沉浸其中的状态是？',
    options: [
      { text: '发呆、游离、进入内心世界', type: 'S', emoji: '🌌' },
      { text: '思考、分析、理解', type: 'J', emoji: '💡' },
      { text: '操作、调配、制作、创造', type: 'P', emoji: '🖐️' },
      { text: '感受、连接、共鸣', type: 'N', emoji: '💜' },
    ],
  },
  {
    id: 10,
    question: '以下哪种经历你最常有？',
    options: [
      { text: '"我好像看到 / 感知到了某个看不见的东西"', type: 'S', emoji: '👁️' },
      { text: '"我好像理解了某个隐藏的规律"', type: 'J', emoji: '🗺️' },
      { text: '"我做了某件事，结果真的改变了"', type: 'P', emoji: '⚡' },
      { text: '"我完全被某种能量或情绪所包围"', type: 'N', emoji: '🌊' },
    ],
  },
  {
    id: 11,
    question: '如果可以选择一种天赋，你会选？',
    options: [
      { text: '看见别人看不见的东西', type: 'S', emoji: '🔮' },
      { text: '解码复杂系统', type: 'J', emoji: '📊' },
      { text: '改变现实结果', type: 'P', emoji: '🪄' },
      { text: '影响人与空间的能量', type: 'N', emoji: '💚' },
    ],
  },
  {
    id: 12,
    question: '哪种状态最令你恐惧？',
    options: [
      { text: '信息太多——无法分辨什么是真实的', type: 'S', emoji: '📡' },
      { text: '混乱且完全没有逻辑', type: 'J', emoji: '🌀' },
      { text: '无法行动或改变任何事情', type: 'P', emoji: '⛓️' },
      { text: '被情绪或他人完全失控地支配', type: 'N', emoji: '🌪️' },
    ],
  },
];

// ───── ENGLISH RESULTS ─────
export const talentResultsEn: Record<TalentType, TalentResult> = {
  S: {
    type: 'S',
    name: 'Seer',
    subtitle: 'Clairvoyance · Intuition · Dreams',
    tagline: 'You are the universe\'s antenna — born to receive signals others cannot see',
    description:
      'You possess a perceptual ability that transcends ordinary consciousness, sensing information before it enters awareness. Dreams hold profound meaning for you, intuition never wavers, and premonitions arise naturally before events unfold. This gift holds tremendous potential in clairvoyance, spiritual communication, and intuitive development. You are not learning a new skill — you are awakening an ability that has always been yours.',
    traits: ['Precise Intuition', 'Frequent Premonitions', 'Vivid & Meaningful Dreams', 'Refined Perception', 'Natural Mediumship'],
    giftSigns: [
      'Before the phone rings, you often already know who\'s calling',
      'Walking into an unfamiliar space, you instantly feel its "energy"',
      'Your dreams sometimes come true, or carry hidden messages',
      'You can feel emotions others have never spoken aloud',
      'You have strong intuitive reactions to certain places or objects',
    ],
    color: '#a78bfa',
    gradientFrom: '#4c1d95',
    gradientTo: '#7c3aed',
    icon: 'ri-eye-2-line',
    courses: [
      { courseId: 8, title: 'Clairvoyance & Spiritual Communication', desc: 'Open channels of clairvoyance and intuitive perception — learn remote viewing, animal communication, and non-physical consciousness connection', tag: 'Top Pick', icon: 'ri-eye-line' },
      { courseId: 1, title: 'Professional Tarot · Level 1 & 2', desc: 'Week 5\'s "Intuitive Reading" module deeply trains accuracy and time-sensing techniques — perfect for Seers', tag: 'Companion', icon: 'ri-magic-line' },
      { courseId: 4, title: 'Crystal & Chakra Healing', desc: 'Systematize your existing energy perception gift through crystal vibration and chakra sensing training', tag: 'Advanced', icon: 'ri-sparkling-line' },
    ],
  },
  J: {
    type: 'J',
    name: 'Interpreter',
    subtitle: 'Tarot · Astrology · Symbols',
    tagline: 'You are the universe\'s translator, turning symbols into life-changing insights',
    description:
      'Your gift lies in decoding — transforming complex symbol systems, star charts, and tarot meanings into clear and powerful life guidance. You deeply resonate with patterns, symbols, and systems, seeing connections others completely miss. Tarot, astrology, runes, and ancient symbolic traditions are your sacred language. In the domain of interpretation, you have the potential to become an extraordinary guide.',
    traits: ['Clear Logical Thinking', 'Symbol Sensitivity', 'Systematic Thinking', 'Deep Analytical Ability', 'Precise Interpretive Power'],
    giftSigns: [
      'The first time you held a tarot deck, you felt you already "knew" it',
      'Star charts give you a mysteriously deep sense of familiarity',
      'You excel at seeing through complexity to the underlying pattern',
      'You love studying mythology, symbols, and ancient wisdom systems',
      'Your readings tend to be spot-on, leaving people stunned',
    ],
    color: '#f4d03f',
    gradientFrom: '#78350f',
    gradientTo: '#d97706',
    icon: 'ri-book-open-line',
    courses: [
      { courseId: 1, title: 'Professional Tarot · Level 1 & 2', desc: 'From Major Arcana to complex spreads — systematically master the symbolic world of all 78 cards', tag: 'Top Pick', icon: 'ri-magic-line' },
      { courseId: 2, title: 'Professional Astrology · Level 1 & 2', desc: 'Learn chart interpretation, planetary energies, and life blueprints — decode the universe\'s unique arrangement for each soul', tag: 'Top Pick', icon: 'ri-planet-line' },
      { courseId: 6, title: 'Nordic Runes · Symbols & Practice', desc: 'Explore the symbolic wisdom of the ancient runic system — master rune divination and bind-rune creation', tag: 'Featured', icon: 'ri-book-open-line' },
    ],
  },
  P: {
    type: 'P',
    name: 'Practitioner',
    subtitle: 'Herbs · Ritual · Alchemy',
    tagline: 'You are the sacred alchemist — turning intention into reality with your hands',
    description:
      'Your power exists in your hands and actions. When you blend herbal oils, design rituals, or craft magical fragrance formulas, that sense of flow and certainty is your most authentic spiritual experience. You don\'t merely perceive the sacred — you create it. Practitioners are often the most powerful ritual workers, herbalists, and spiritual craft masters; reality truly changes through your hands.',
    traits: ['Powerful Hands-On Ability', 'Natural Ritual Sense', 'Rich Creativity', 'Keen Attention to Detail', 'Exceptional Transformative Ability'],
    giftSigns: [
      'Things you make by hand always carry a unique and special energy',
      'You have an intuitive understanding of plants, scents, and textures',
      'Designing rituals or sacred spaces brings you deep joy',
      'You enter a deep "flow state" while working, losing track of time',
      'People who use what you\'ve made often say it feels especially powerful',
    ],
    color: '#34d399',
    gradientFrom: '#064e3b',
    gradientTo: '#059669',
    icon: 'ri-flask-line',
    courses: [
      { courseId: 3, title: 'Practical Herbal Magic · Level 1 & 2', desc: 'From herbal basics to potion brewing, sachet blending, and magical candle making — create sacred transformational energy with your hands', tag: 'Top Pick', icon: 'ri-flask-line' },
      { courseId: 5, title: 'Moon Magic · Ritual Practice', desc: 'Design and lead New Moon manifestation and Full Moon release ceremonies — transform cosmic rhythms into real-world magical change', tag: 'Top Pick', icon: 'ri-moon-line' },
      { courseId: 4, title: 'Crystal & Chakra Healing', desc: 'Learn crystal grid layouts and energy charging techniques — build sacred healing spaces filled with divine intention', tag: 'Advanced', icon: 'ri-sparkling-line' },
    ],
  },
  N: {
    type: 'N',
    name: 'Healer',
    subtitle: 'Energy · Touch · Field',
    tagline: 'You are a living energy field — your very presence is healing',
    description:
      'Your perception of energy is natural and effortless. The emotions of others, the vibration of spaces, invisible field shifts — all leave clear imprints in your body. This heightened sensitivity, properly trained, can transform into powerful healing ability. Touch healing, field adjustment, and chakra healing are not things you need to work hard to learn — they are gifts you need to systematically master and continuously express.',
    traits: ['Deep Empathy', 'Energy Sensitivity', 'Healing Instinct', 'Field Perception', 'Natural Healing Constitution'],
    giftSigns: [
      'When you are present, others unconsciously relax and open up',
      'You can sense energetic blockages in others\' bodies or emotions',
      'Entering certain spaces, you have strong physical reactions',
      'Sometimes touching objects, you can sense their energetic history',
      'After healing or supporting others, you need time to clear your own energy',
    ],
    color: '#f9a8d4',
    gradientFrom: '#831843',
    gradientTo: '#db2777',
    icon: 'ri-heart-2-line',
    courses: [
      { courseId: 7, title: 'Energy Reset & Touch Healing', desc: 'Guide the body into deep stillness through touch techniques — learn pressure point systems and energy release logic, become a certified healer', tag: 'Top Pick', icon: 'ri-fire-line' },
      { courseId: 4, title: 'Crystal & Chakra Healing', desc: 'Explore the seven-chakra system and crystal healing layouts — restore others\' inner balance and flow through energetic resonance', tag: 'Top Pick', icon: 'ri-sparkling-line' },
      { courseId: 8, title: 'Clairvoyance & Spiritual Communication', desc: 'Further develop field perception and consciousness connection — elevate your natural energy sensitivity into precise spiritual communication', tag: 'Advanced', icon: 'ri-compass-3-line' },
    ],
  },
};

// ───── CHINESE RESULTS ─────
export const talentResultsZh: Record<TalentType, TalentResult> = {
  S: {
    type: 'S',
    name: '感知者',
    subtitle: '千里眼 · 直觉 · 梦境',
    tagline: '你是宇宙的天线——天生接收他人看不见的信号',
    description:
      '你拥有超越普通意识的感知力，能在信息进入意识之前便感知到它的存在。梦境对你意义深远，直觉从不失灵，预感往往在事情发生前便自然浮现。这份天赋在千里眼、灵性沟通与直觉开发领域拥有巨大潜力。你并不是在学习一项新技能——你是在唤醒一种本就属于你的能力。',
    traits: ['精准直觉', '频繁预感', '生动且有意义的梦境', '细腻感知', '天然通灵体质'],
    giftSigns: [
      '电话铃声响起前，你往往已经知道是谁打来的',
      '走进陌生空间，你能立刻感受到那里的"能量"',
      '你的梦有时会成真，或携带隐藏信息',
      '你能感受到他人未曾言说的情绪',
      '你对某些地方或物件有强烈的直觉反应',
    ],
    color: '#a78bfa',
    gradientFrom: '#4c1d95',
    gradientTo: '#7c3aed',
    icon: 'ri-eye-2-line',
    courses: [
      { courseId: 8, title: '千里眼与灵性沟通课程', desc: '开启千里眼与直觉感知通道，学习远程观察、动物沟通及非物质意识连接——唤醒你与生俱来的感知天赋', tag: '首选推荐', icon: 'ri-eye-line' },
      { courseId: 1, title: '专业塔罗课程 · 一阶 & 二阶', desc: '第五周"直觉解读"模块深度训练准确度与时间感知技术——完美配合感知者体质', tag: '配套推荐', icon: 'ri-magic-line' },
      { courseId: 4, title: '水晶与脉轮疗愈课程', desc: '通过水晶振动属性与脉轮感知训练，将你已有的能量感知天赋系统化，使其更稳定、更精准', tag: '进阶', icon: 'ri-sparkling-line' },
    ],
  },
  J: {
    type: 'J',
    name: '解读者',
    subtitle: '塔罗 · 占星 · 符号',
    tagline: '你是宇宙语言的翻译者，将符号转化为改变人生的洞见',
    description:
      '你的天赋在于解码——将复杂的符号系统、星盘与塔罗含义转化为清晰有力的人生指引。你对规律、符号和系统有深度共鸣，能看见他人完全忽略的关联。塔罗、占星、如尼文与古代符号传统是你的神圣语言。在解读的领域，你拥有成为卓越引导者的潜力。',
    traits: ['清晰的逻辑思维', '符号敏感度', '系统性思维', '深度分析能力', '精准解读力'],
    giftSigns: [
      '第一次拿到塔罗牌时，你感觉自己已经"认识"它们了',
      '星盘给你一种神秘的深度熟悉感',
      '你擅长看穿复杂情况背后的本质与隐藏规律',
      '你热爱研究神话、符号与古代智慧体系',
      '你的解读往往一针见血，令对方震惊',
    ],
    color: '#f4d03f',
    gradientFrom: '#78350f',
    gradientTo: '#d97706',
    icon: 'ri-book-open-line',
    courses: [
      { courseId: 1, title: '专业塔罗课程 · 一阶 & 二阶', desc: '从大秘仪到复杂牌阵，系统掌握全部78张牌的符号世界，建立你的专业解读框架', tag: '首选推荐', icon: 'ri-magic-line' },
      { courseId: 2, title: '专业占星课程 · 一阶 & 二阶', desc: '学习星盘解读、行星能量与人生蓝图——解码宇宙为每个灵魂做出的独特安排', tag: '首选推荐', icon: 'ri-planet-line' },
      { courseId: 6, title: '北欧如尼文课程 · 符号与实践', desc: '探索古代如尼文系统的符号智慧，掌握如尼文占卜与绑文制作的完整技术', tag: '精选', icon: 'ri-book-open-line' },
    ],
  },
  P: {
    type: 'P',
    name: '实践者',
    subtitle: '草药 · 仪式 · 炼金',
    tagline: '你是神圣炼金师——用双手将意念转化为现实',
    description:
      '你的力量存在于双手与行动之中。当你调配草药精油、设计仪式或制作魔法香氛配方时，那种流动感与确定感就是你最真实的灵性体验。你不仅仅是感知神圣——你创造神圣。实践者往往是最强大的仪式工作者、草药师和灵性手工大师；现实真的因你的双手而改变。',
    traits: ['强大的动手能力', '天然仪式感', '丰富创造力', '注重细节', '卓越转化能力'],
    giftSigns: [
      '你亲手制作的东西总携带一种特殊而独特的能量',
      '你对植物、气味和质地有直觉性的理解',
      '设计仪式或神圣空间给你带来极大愉悦',
      '你在工作中会进入深度"心流状态"，忘记时间',
      '使用你所制作之物的人，往往反馈效果格外强大',
    ],
    color: '#34d399',
    gradientFrom: '#064e3b',
    gradientTo: '#059669',
    icon: 'ri-flask-line',
    courses: [
      { courseId: 3, title: '实用草药魔法课程 · 一阶 & 二阶', desc: '从草药基础到药水酿造、香包调配、魔法蜡烛制作——用双手创造神圣的转化能量', tag: '首选推荐', icon: 'ri-flask-line' },
      { courseId: 5, title: '月亮魔法课程 · 仪式实践', desc: '设计并主持新月显化与满月释放仪式——将宇宙节律转化为现实世界的魔法改变', tag: '首选推荐', icon: 'ri-moon-line' },
      { courseId: 4, title: '水晶与脉轮疗愈课程', desc: '学习水晶阵列布局与能量充电技术——亲手打造充满神圣意念的疗愈空间', tag: '进阶', icon: 'ri-sparkling-line' },
    ],
  },
  N: {
    type: 'N',
    name: '疗愈者',
    subtitle: '能量 · 触碰 · 场域',
    tagline: '你本身就是一个活着的能量场——你的存在本身就是疗愈',
    description:
      '你对能量的感知是自然而然、毫不费力的。他人的情绪、空间的振动、看不见的场域变化都在你的身体里留下清晰印记。这种高度敏感性经过恰当训练，可以转化为强大的疗愈能力。触碰疗愈、场域调整与脉轮疗愈并不是你需要努力学习的东西——它们是你需要系统掌握并持续表达的天赋。',
    traits: ['高度共情力', '能量敏感度', '疗愈本能', '场域感知', '天然疗愈体质'],
    giftSigns: [
      '当你在场时，他人会不自觉地放松和敞开',
      '你能感知他人身体或情绪中的能量堵塞',
      '走进某些特定空间时，你有强烈的身体反应',
      '有时触摸物件时，你能感知到其能量历史',
      '疗愈或支持他人后，你需要时间清理自己的能量',
    ],
    color: '#f9a8d4',
    gradientFrom: '#831843',
    gradientTo: '#db2777',
    icon: 'ri-heart-2-line',
    courses: [
      { courseId: 7, title: '能量重置与触碰疗愈课程', desc: '通过触碰技术引导身体进入深度静止。学习压力点系统与能量释放逻辑，成为认证疗愈师', tag: '首选推荐', icon: 'ri-fire-line' },
      { courseId: 4, title: '水晶与脉轮疗愈课程', desc: '深度探索七大脉轮体系与水晶疗愈布局——通过能量共振恢复他人内在的平衡与流动', tag: '首选推荐', icon: 'ri-sparkling-line' },
      { courseId: 8, title: '千里眼与灵性沟通课程', desc: '进一步发展场域感知与意识连接——将你天然的能量敏感度提升为精准的灵性沟通能力', tag: '进阶', icon: 'ri-compass-3-line' },
    ],
  },
};

// ───── LEGACY EXPORT (backward compat) ─────
export const questions = questionsZh;
export const talentResults = talentResultsZh;
